import { useState, useEffect } from "react";
import { Plus, TrashSimple, ArrowUp, ArrowDown, LinkSimple, Play, Sparkle, DotsSixVertical, BookmarkSimple, CaretDown, CaretLeft, CaretRight, FloppyDisk } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Slider } from "../components/ui/slider";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { api } from "../lib/api";
import { fileColor } from "../lib/files";

const OPTION_DEFS = [
  { key: "exact", label: "esatta" },
  { key: "ignore_case", label: "no maiuscole" },
  { key: "ignore_special", label: "no speciali" },
  { key: "contains", label: "contiene" },
  { key: "fuzzy", label: "assomiglia" },
];

export function newLevel() {
  return {
    id: Math.random().toString(36).slice(2),
    label: "Nuovo livello",
    columns: { "1": [], "2": [], "3": [] },
    options: { exact: true, ignore_case: false, ignore_special: false, contains: false, fuzzy: false },
    fuzzy_threshold: 80,
  };
}

function colReliability(file, col) {
  const rows = file.rows || [];
  let filled = 0; const samples = [];
  rows.forEach((r) => {
    const v = r[col];
    if (v !== null && v !== undefined && String(v).trim() !== "") {
      filled++;
      if (samples.length < 3) samples.push(String(v));
    }
  });
  const total = rows.length || 1;
  return { rate: Math.round((filled / total) * 100), samples };
}

function dotColor(rate) {
  if (rate >= 90) return "hsl(142,70%,40%)";
  if (rate >= 60) return "hsl(38,90%,45%)";
  return "hsl(0,72%,50%)";
}

function ColumnPicker({ file, selected, onToggle, onMove, canLeft, canRight }) {
  const color = fileColor(file.slot);
  return (
    <div className="rounded-lg border border-border p-3 bg-muted/40" data-testid={`level-file-${file.slot}-picker`}>
      <div className="flex items-center justify-between mb-2">
        <button disabled={!canLeft} onClick={() => onMove(-1)} data-testid={`source-left-${file.slot}`}
          className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-20" title="Sposta a sinistra"><CaretLeft size={14} /></button>
        <span className="text-xs font-semibold truncate px-1" style={{ color }}>{file.name}</span>
        <button disabled={!canRight} onClick={() => onMove(1)} data-testid={`source-right-${file.slot}`}
          className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-20" title="Sposta a destra"><CaretRight size={14} /></button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {file.columns.map((c) => {
          const on = selected.includes(c);
          const rel = colReliability(file, c);
          return (
            <button key={c} onClick={() => onToggle(c)} data-testid={`col-chip-f${file.slot}-${c}`}
              title={`Compilata ${rel.rate}% · es: ${rel.samples.join(", ") || "—"}`}
              className="grid-font text-xs px-2 py-1 rounded-md border transition-colors flex items-center gap-1.5"
              style={on ? { background: color, color: "#fff", borderColor: color }
                        : { background: "hsl(var(--secondary))", borderColor: "hsl(var(--border))" }}>
              <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: on ? "#fff" : dotColor(rel.rate) }} />
              {c}
              <span className="opacity-60">{rel.rate}%</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function LevelCard({ level, index, total, files, update, remove, move, dnd }) {
  const toggleCol = (slot, col) => {
    const cur = (level.columns && level.columns[String(slot)]) || [];
    const next = cur.includes(col) ? cur.filter((c) => c !== col) : [...cur, col];
    update({ ...level, columns: { ...(level.columns || {}), [String(slot)]: next } });
  };
  const toggleOpt = (k) => update({ ...level, options: { ...level.options, [k]: !level.options[k] } });
  const order = ((level.source_order || []).filter((s) => files.some((f) => f.slot === s)));
  files.forEach((f) => { if (!order.includes(f.slot)) order.push(f.slot); });
  const orderedFiles = order.map((s) => files.find((f) => f.slot === s)).filter(Boolean);
  const moveSource = (slot, dir) => {
    const cur = [...order];
    const i = cur.indexOf(slot);
    const j = i + dir;
    if (j < 0 || j >= cur.length) return;
    [cur[i], cur[j]] = [cur[j], cur[i]];
    update({ ...level, source_order: cur });
  };

  return (
    <div draggable onDragStart={(e) => dnd.onDragStart(e, index)} onDragOver={(e) => dnd.onDragOver(e, index)}
      onDrop={(e) => dnd.onDrop(e, index)} onDragEnd={dnd.onDragEnd}
      className={`metallic rounded-xl border border-border p-4 fade-up ${dnd.dragIndex === index ? "dragging" : ""} ${dnd.overIndex === index ? "drag-over" : ""}`}
      data-testid={`level-card-${index}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="cursor-grab active:cursor-grabbing text-muted-foreground" data-testid={`level-drag-${index}`}><DotsSixVertical size={18} /></span>
        <span className="grid-font text-xs px-2 py-1 rounded bg-primary/15 text-primary font-semibold">L{index + 1}</span>
        <Input value={level.label} onChange={(e) => update({ ...level, label: e.target.value })}
          data-testid={`level-label-${index}`} className="bg-muted h-8 max-w-[220px]" />
        <div className="ml-auto flex items-center gap-1">
          <button disabled={index === 0} onClick={() => move(index, -1)} data-testid={`level-up-${index}`}
            className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30"><ArrowUp size={16} /></button>
          <button disabled={index === total - 1} onClick={() => move(index, 1)} data-testid={`level-down-${index}`}
            className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30"><ArrowDown size={16} /></button>
          <button onClick={() => remove(index)} data-testid={`level-remove-${index}`}
            className="p-1 text-muted-foreground hover:text-destructive"><TrashSimple size={16} /></button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
        {orderedFiles.map((f, oi) => (
          <ColumnPicker key={f.slot} file={f} selected={(level.columns && level.columns[String(f.slot)]) || []}
            onToggle={(c) => toggleCol(f.slot, c)}
            onMove={(dir) => moveSource(f.slot, dir)} canLeft={oi > 0} canRight={oi < orderedFiles.length - 1} />
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {OPTION_DEFS.map((o) => (
          <button key={o.key} onClick={() => toggleOpt(o.key)} data-testid={`level-${index}-opt-${o.key}`}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${level.options[o.key] ? "bg-[hsl(var(--cyan)/0.15)] text-[hsl(190,90%,30%)] border-[hsl(var(--cyan)/0.6)]" : "bg-secondary/40 border-border text-muted-foreground"}`}>
            {o.label}
          </button>
        ))}
        {level.options.contains && orderedFiles.length >= 2 && (
          <span data-testid={`level-${index}-contains-dir`}
            className="text-xs px-2.5 py-1 rounded-full border border-[hsl(var(--gold)/0.6)] bg-[hsl(var(--gold)/0.12)] text-[hsl(38,80%,38%)] font-medium">
            {orderedFiles.length ? `${orderedFiles[0].name} contiene ${orderedFiles.slice(1).map((f) => f.name).join(", ")}` : ""}
          </span>
        )}
      </div>

      {level.options.fuzzy && (
        <div className="mt-3 flex items-center gap-3">
          <span className="text-xs text-muted-foreground whitespace-nowrap">Soglia similarità</span>
          <Slider value={[level.fuzzy_threshold]} min={50} max={100} step={1}
            onValueChange={(v) => update({ ...level, fuzzy_threshold: v[0] })}
            data-testid={`level-${index}-fuzzy-slider`} className="max-w-[240px]" />
          <span className="grid-font text-sm text-[hsl(var(--cyan))] w-10">{level.fuzzy_threshold}%</span>
        </div>
      )}
    </div>
  );
}

export function RuleStack({ job, setJob, onMatched, levels, setLevels }) {
  const [running, setRunning] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const files = job.files;

  const withNewIds = (lvls) => lvls.map((l) => ({ ...l, id: Math.random().toString(36).slice(2) }));

  const suggestKeys = async () => {
    try {
      const r = await api.suggestKeys(job.id);
      setLevels(withNewIds(r.levels));
      toast.success(`${r.levels.length} chiave/i suggerite da RowTool`);
    } catch (e) {
      toast.message(e.response?.data?.detail || "Nessun suggerimento disponibile");
    }
  };

  const update = (i, l) => setLevels(levels.map((x, idx) => (idx === i ? l : x)));
  const remove = (i) => setLevels(levels.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= levels.length) return;
    const copy = [...levels];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setLevels(copy);
  };

  const dnd = {
    dragIndex, overIndex,
    onDragStart: (e, i) => { setDragIndex(i); e.dataTransfer.effectAllowed = "move"; },
    onDragOver: (e, i) => { e.preventDefault(); setOverIndex(i); },
    onDrop: (e, i) => {
      e.preventDefault();
      if (dragIndex === null || dragIndex === i) { setOverIndex(null); return; }
      const copy = [...levels];
      const [moved] = copy.splice(dragIndex, 1);
      copy.splice(i, 0, moved);
      setLevels(copy);
      setDragIndex(null); setOverIndex(null);
    },
    onDragEnd: () => { setDragIndex(null); setOverIndex(null); },
  };

  const runMatch = async () => {
    setRunning(true);
    try {
      const data = await api.match(job.id, levels);
      onMatched(data);
      const j = await api.getJob(job.id);
      setJob(j);
      toast.success(`Abbinamento completato · ${data.stats.total} righe`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore abbinamento");
    } finally {
      setRunning(false);
    }
  };

  if (files.length === 0) return null;

  return (
    <section data-testid="rule-stack-section">
      <div className="flex items-center gap-3 mb-4">
        <span className="stage-num">2</span>
        <h2 className="text-xl font-head font-bold">Motore di abbinamento</h2>
        <LinkSimple size={18} className="text-primary" />
      </div>

      {files.length < 2 && (
        <div className="mb-3 text-sm text-muted-foreground flex items-center gap-2">
          <Sparkle size={16} className="text-[hsl(var(--gold))]" />
          Con un solo file puoi comunque usare le verifiche e i tool AI dopo l'abbinamento.
        </div>
      )}

      <div className="space-y-3">
        {levels.map((l, i) => (
          <LevelCard key={l.id} level={l} index={i} total={levels.length} files={files}
            update={(nl) => update(i, nl)} remove={remove} move={move} dnd={dnd} />
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-4">
        <Button variant="outline" onClick={() => setLevels([...levels, newLevel()])}
          data-testid="add-level-btn" className="gap-2">
          <Plus size={16} /> Aggiungi livello
        </Button>

        {files.length >= 2 && (
          <Button variant="outline" onClick={suggestKeys} data-testid="suggest-keys-btn"
            className="gap-2 border-[hsl(var(--accent)/0.5)] text-[hsl(var(--accent))]">
            <Sparkle size={16} weight="fill" /> Suggerisci chiavi
          </Button>
        )}

        <Button onClick={runMatch} disabled={running} data-testid="run-match-btn"
          className="gap-2 bg-primary hover:bg-primary/90 ml-auto">
          <Play size={16} weight="fill" /> {running ? "Abbinamento..." : "Esegui abbinamento"}
        </Button>
      </div>
    </section>
  );
}
